/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import StackGrid from '../StackGrid';
import { createAgreement, fetchAgreement, editAgreement } from '../../actions/agreements';
import { fetchCalculationTypes } from '../../actions/calculationTypes';
import { fetchContract } from '../../actions/contracts';
import TextField from '../TextField';
import Dropdown from '../Dropdown';
import DatePicker from '../DatePicker';
import FilterForm from '../FilterForm';
import FilterList from '../FilterList';
import ProductsList from '../ProductsList';
import ProductAutosuggest from '../ProductAutosuggest';
import SectionHeader from '../SectionHeader';
import TextButton from '../TextButton';
import { useAgreementForms } from '../../context/agreement-forms';
import styles from './AgreementForm.style';
import { fetchDigitizedContract } from '../../actions/digitizedContracts';
import {
  normalizeDate,
  addTimeToDate,
  removePunctuation,
  capitalizeWordsInString,
  isFieldLengthTooLong,
  formatDateString,
} from '../../utils';

const AgreementSchema = Yup.object().shape({
  name: Yup.string().max(32).required('Required').label('Name'),
  rate: Yup.number().integer().min(1).max(100).required('Required').label('Rate'),
  calculation: Yup.string().required('Required'),
  effectiveDate: Yup.date().required('Required'),
  terminationDate: Yup.date().required('Required'),
  filters: Yup.array(Yup.string().required('Required')),
  products: Yup.array(Yup.number().required('Required')),
});

function extractAttributes(attributesList) {
  const effectiveDate = normalizeDate(attributesList['Effective Date'].valueString);
  const royaltyRate = attributesList['CSV Royalty Rate'].valueString;
  const calculationType = capitalizeWordsInString(attributesList['CSV Calculation'].valueString);
  const expirationDate = addTimeToDate(effectiveDate, attributesList.Term.valueString);
  return {
    calculationType,
    royaltyRate,
    effectiveDate,
    expirationDate,
  };
}

const AgreementForm = React.forwardRef(
  ({ onSubmit, onSuccess, contractAddress, address, contractId, uniqueId, reviewMode }, ref) => {
    const [formAttributes, setFormAttributes] = useState({});
    const [agreementAddress, setAgreementAddress] = useState(address || '');
    const { registerForm, unRegisterForm } = useAgreementForms();
    const dispatch = useDispatch();
    const formRef = ref || useRef(null);
    const agreement = useSelector(state => get(state, `entities.agreements.${address}`, {}));
    const calculationContract = useSelector(state =>
      get(state, `entities.calculationTypes.${agreement && agreement.calculation}.contract`, ''),
    );
    const [showFilterForm, setShowFilterForm] = useState(false);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [filters, setFilters] = useState([]);
    const [products, setProducts] = useState([]);
    const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, {}));
    const digitizedContract = useSelector(state =>
      get(state, `entities.digitizedcontracts.${contractId}`, {}),
    );
    const calculationTypes = useSelector(state =>
      Object.values(get(state, `entities.calculationTypes`, {})),
    ).map(calculationType => ({
      key: calculationType.contract,
      text: calculationType.name,
      address: calculationType.address,
    }));

    useEffect(() => {
      dispatch(fetchCalculationTypes());
    }, [dispatch]);

    useEffect(() => {
      dispatch(fetchContract(contractAddress));
    }, [dispatch, contractAddress]);

    useEffect(() => {
      if (contractId) {
        dispatch(fetchDigitizedContract(contractId));
      }
    }, [dispatch, contractId]);

    useEffect(() => {
      async function getAgreement() {
        const response = await dispatch(
          fetchAgreement(contractAddress, agreementAddress, { useCache: false }),
        );
        // when agreement request resolves, update local filter & product state
        setProducts(Object.values(get(response, `payload.entities.products`, {})));
        setFilters(Object.values(get(response, `payload.entities.filters`, {})));
      }
      if (agreementAddress) {
        getAgreement();
      }
    }, [dispatch, contractAddress, agreementAddress]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (!ref) {
        // register the form so parent components can control it via the ref passed in
        const uniqueIdentifier = address || uniqueId;
        registerForm(uniqueIdentifier, formRef);

        return () => {
          unRegisterForm(uniqueIdentifier);
        };
      }
    }, []); // eslint-disable-line

    const { attributes } = digitizedContract;
    if (attributes && Object.keys(formAttributes).length === 0) {
      let { contractName } = digitizedContract;
      contractName = removePunctuation(contractName);
      contractName = isFieldLengthTooLong(contractName, 32)
        ? `${contractName.substring(0, 19).trim()} Xbox One TLA`
        : contractName;
      const extractedAttributes = extractAttributes(attributes);
      const { calculationType, royaltyRate, effectiveDate, expirationDate } = extractedAttributes;
      setFormAttributes({
        ...formAttributes,
        contractName,
        calculationType,
        royaltyRate,
        effectiveDate,
        expirationDate,
      });
    }

    const initialValues = omit(
      merge(
        {
          name: formAttributes.contractName,
          rate: formAttributes.royaltyRate,
          effectiveDate: formAttributes.effectiveDate,
          terminationDate: formAttributes.expirationDate,
        },
        agreement,
        {
          products: isEmpty(agreement) ? [] : agreement.products,
          filters: isEmpty(agreement) ? [] : agreement.filters,
          calculation: formAttributes.calculationType || calculationContract,
        },
      ),
      // remove agreement address so it doesn't get sent in request body
      'address',
    );
    initialValues.effectiveDate = formatDateString(initialValues.effectiveDate);
    initialValues.terminationDate = formatDateString(initialValues.terminationDate);

    return (
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={AgreementSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={
          onSubmit ||
          (async (newValues, { setErrors }) => {
            let response;
            if (agreementAddress) {
              response = await dispatch(
                editAgreement(contractAddress, agreementAddress, newValues),
              );
            } else {
              response = await dispatch(createAgreement(contractAddress, newValues));
            }

            if (response.error && response.payload.message) {
              const error = new Error(
                get(response, 'payload.body.reason') || get(response, 'payload.body.message'),
              );
              error.payload = response.payload.message;
              setErrors({ form: error.message });
              throw error;
            }

            // just in case you're on a page submitting multiple agreements, and one fails,
            // we need to make sure the next time submission occurs, it edits the existing
            // agreement. So we set the resulting agreement address here, for next submission
            const newAgreementAddress = get(response, 'payload.result');
            if (!agreementAddress && newAgreementAddress) {
              setAgreementAddress(newAgreementAddress);
            }

            response = get(
              response,
              `payload.entities.agreements.${get(response, 'payload.result')}`,
              {},
            );
            await onSuccess(response);

            return response;
          })
        }
      >
        {({ errors, isSubmitting, values, setFieldValue }) => {
          const handleFilterCreation = filter => {
            setShowFilterForm(false);
            setFilters([...filters, filter]);
            setFieldValue('filters', [...values.filters, filter.address]);
          };

          const handleRemoveFilter = addressToRemove => {
            const updatedFilters = filters.filter(filter => filter.address !== addressToRemove);
            setFilters(updatedFilters);
            setFieldValue(
              'filters',
              updatedFilters.map(filter => filter.address),
            );
          };

          const handleAddProduct = product => {
            setShowProductSearch(false);
            setProducts([...products, { tokenId: product.value, name: product.label }]);
            setFieldValue('products', [...values.products, product.value]);
          };

          const handleRemoveProduct = productToRemove => {
            const updatedProducts = products.filter(product => product.tokenId !== productToRemove);
            setProducts(updatedProducts);
            setFieldValue(
              'products',
              updatedProducts.map(product => product.tokenId),
            );
          };

          return (
            <>
              {contract && (
                <Form noValidate>
                  <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                    {errors.form && (
                      <MessageBar
                        messageBarType={MessageBarType.error}
                        messageBarIconProps={{ iconName: 'Info' }}
                        isMultiline
                        dismissButtonAriaLabel="Close"
                        nowrap
                      >
                        {errors.form}
                      </MessageBar>
                    )}

                    <Field
                      name="name"
                      label="Name"
                      component={TextField}
                      required
                      disabled={reviewMode || isSubmitting}
                    />
                    <Field
                      name="calculation"
                      label="Calculation Type"
                      component={Dropdown}
                      options={calculationTypes}
                      required
                      preventDefaultOnEnter
                      disabled={!!(reviewMode || address || isSubmitting)}
                    />
                    <Field
                      name="rate"
                      type="number"
                      min="0"
                      max="100"
                      step="5"
                      label="Royalty Rate"
                      component={TextField}
                      required
                      disabled={reviewMode || isSubmitting}
                    />
                    <Field
                      name="effectiveDate"
                      label="Effective Date"
                      type="date"
                      component={DatePicker}
                      minDate={new Date(formatDateString(contract.effectiveDate))}
                      maxDate={new Date(formatDateString(contract.terminationDate))}
                      required
                      preventDefaultOnEnter
                      disabled={reviewMode || isSubmitting}
                    />
                    <Field
                      name="terminationDate"
                      label="Expiration Date"
                      type="date"
                      component={DatePicker}
                      minDate={new Date(formatDateString(contract.effectiveDate))}
                      maxDate={new Date(formatDateString(contract.terminationDate))}
                      required
                      preventDefaultOnEnter
                      disabled={reviewMode || isSubmitting}
                    />
                    <section nowrap="true">
                      <SectionHeader noSpacing={products.length > 0}>Products</SectionHeader>
                      {errors.products && (
                        <MessageBar
                          messageBarType={MessageBarType.error}
                          messageBarIconProps={{ iconName: 'Info' }}
                          isMultiline
                          dismissButtonAriaLabel="Close"
                          nowrap
                        >
                          {errors.products}
                        </MessageBar>
                      )}
                      {products.length === 0 && reviewMode && (
                        <Text as="p" block variant="small">
                          No products have been associated with this royalty fee
                        </Text>
                      )}
                      {products.length > 0 && typeof products[0] === 'object' && (
                        <ProductsList
                          items={products}
                          onRemove={handleRemoveProduct}
                          disabled={isSubmitting || showFilterForm}
                          removable={!reviewMode}
                          enableView
                          disableRowLink
                          isHeaderVisible={false}
                        />
                      )}
                      {showProductSearch && (
                        <div className={styles.toggleFormContainer}>
                          <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            title="Close"
                            ariaLabel="Close"
                            className={styles.toggleCloseButton}
                            onClick={() => setShowProductSearch(false)}
                          />
                          <ProductAutosuggest
                            excludeProducts={products}
                            className={styles.productAutosuggest}
                            onClick={handleAddProduct}
                          />
                        </div>
                      )}
                      {!reviewMode && (
                        <TextButton
                          noPadding
                          disabled={showProductSearch}
                          className={styles.productAddButton}
                          onClick={() => setShowProductSearch(true)}
                        >
                          Associate a Product
                        </TextButton>
                      )}
                    </section>
                    <section nowrap="true">
                      <SectionHeader noSpacing={filters.length > 0}>Filters</SectionHeader>
                      {errors.filters && (
                        <MessageBar
                          messageBarType={MessageBarType.error}
                          messageBarIconProps={{ iconName: 'Info' }}
                          isMultiline
                          dismissButtonAriaLabel="Close"
                          nowrap
                        >
                          {errors.filters}
                        </MessageBar>
                      )}
                      {filters.length === 0 && reviewMode && (
                        <Text as="p" block variant="small">
                          No filters have been associated with this royalty fee
                        </Text>
                      )}
                      {filters.length > 0 && typeof filters[0] === 'object' && (
                        <FilterList
                          filters={filters}
                          products={products}
                          onRemove={!reviewMode ? handleRemoveFilter : null}
                        />
                      )}
                      {showFilterForm && (
                        <div className={styles.toggleFormContainer}>
                          <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            title="Close"
                            ariaLabel="Close"
                            className={styles.toggleCloseButton}
                            onClick={() => setShowFilterForm(false)}
                          />
                          <FilterForm
                            onSuccess={handleFilterCreation}
                            products={products.map(product => ({
                              key: product.tokenId,
                              text: product.name,
                            }))}
                          />
                        </div>
                      )}
                      {!reviewMode && (
                        <TextButton
                          noPadding
                          disabled={showFilterForm}
                          className={styles.filterAddButton}
                          onClick={() => setShowFilterForm(true)}
                        >
                          Add a filter
                        </TextButton>
                      )}
                    </section>
                  </StackGrid>
                </Form>
              )}
            </>
          );
        }}
      </Formik>
    );
  },
);

AgreementForm.propTypes = {
  contractAddress: PropTypes.string.isRequired,
  contractId: PropTypes.string,
  address: PropTypes.string,
  uniqueId: PropTypes.string,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  reviewMode: PropTypes.bool,
};

AgreementForm.defaultProps = {
  address: null,
  uniqueId: null,
  onSubmit: null,
  contractId: null,
  onSuccess: () => {},
  reviewMode: false,
};

export default AgreementForm;
